import Backdrop from '@mui/material/Backdrop'
import { styled } from '@mui/material/styles'
import EasySpeech from 'easy-speech'
import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TtsContext, TtsContextType } from '../contexts/TtsContext'
import { captureError } from '../utils/sentry'
import { getTtsVoice, isTtsCancelError, ttsInitialized } from '../utils/tts'
import LoadingSpinner from './LoadingSpinner'
import TtsHelp from './TtsHelp'
import TtsPlayer from './TtsPlayer'

const TTS_TIMEOUT = 2000
const TTS_RETRY_INTERVAL = 250

const StyledBackdrop = styled(Backdrop)`
  z-index: 100;
`

type TtsContainerProps = {
  languageCode: string
  children: ReactElement
}

const TtsContainer = ({ languageCode, children }: TtsContainerProps): ReactElement | null => {
  const { t } = useTranslation()
  const afterStopRef = useRef<(() => void) | null>(null)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [visible, setVisible] = useState(false)
  const [sentences, setSentences] = useState<string[]>([])
  const [sentenceIndex, setSentenceIndex] = useState(0)
  const [showHelp, setShowHelp] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const title = sentences[0] || t($ => $.tts.error.nothingToRead.title)
  const canRead = sentences.length > 1

  const initializeTts = useCallback(() => {
    setIsInitializing(true)
    EasySpeech.init({ maxTimeout: TTS_TIMEOUT, interval: TTS_RETRY_INTERVAL })
      .then(() => {
        setIsInitializing(false)
        setVisible(true)
      })
      .catch(() => {
        setIsInitializing(false)
        setShowHelp(true)
      })
  }, [])

  const stopPlayer = useCallback((afterStop: () => void = () => undefined) => {
    if (ttsInitialized()) {
      // The end event is always emitted if the current utterance is stopped (incl. finish, cancel and pause)
      // The end event is only emitted after some time, such that we can only start the next utterance after to avoid autoplaying the next utterance
      // https://developer.mozilla.org/en-US/docs/Web/API/SpeechSynthesisUtterance/end_event
      afterStopRef.current = afterStop
      EasySpeech.cancel()
    }
  }, [])

  const stop = useCallback(() => {
    setSentenceIndex(0)
    setIsPlaying(false)
    stopPlayer()
  }, [stopPlayer])

  const pause = () => {
    setIsPlaying(false)
    stopPlayer()
  }

  const play = useCallback(
    async (index = sentenceIndex) => {
      const voice = getTtsVoice(languageCode)
      if (!voice) {
        setShowHelp(true)
        return
      }

      const safeIndex = Math.max(0, index)
      const sentence = sentences[safeIndex]

      if (sentence === undefined) {
        stop()
        return
      }

      setSentenceIndex(safeIndex)

      try {
        setIsPlaying(true)
        await EasySpeech.speak({
          text: sentence,
          voice,
          volume: 0.6,
          rate: 0.8,
          end: () => {
            // We only want to play the next sentence if the current utterance is finished and the user did not stop it manually
            // Otherwise, execute afterStop (play previous or next sentence for playPrevious/Next or noop for stop)
            if (afterStopRef.current) {
              afterStopRef.current()
              afterStopRef.current = null
            } else {
              play(safeIndex + 1)
            }
          },
        })
      } catch (error) {
        // Chrome and Safari throw an interrupted/canceled error event on cancel instead of emitting an end event
        if (isTtsCancelError(error)) {
          if (afterStopRef.current) {
            afterStopRef.current()
            afterStopRef.current = null
          }
        } else {
          captureError(error)
        }
      }
    },
    [sentenceIndex, setIsPlaying, sentences, stop, languageCode],
  )

  const startPlaying = () => {
    afterStopRef.current = null
    play()
  }

  const close = () => {
    setVisible(false)
    setShowHelp(false)
    stop()
  }

  const playPrevious = () => (isPlaying ? stopPlayer(() => play(sentenceIndex - 1)) : play(sentenceIndex - 1))
  const playNext = () => (isPlaying ? stopPlayer(() => play(sentenceIndex + 1)) : play(sentenceIndex + 1))

  const updateSentences = useCallback(
    (newSentences: string[]) => {
      setSentences(newSentences)
      stop()
    },
    [stop],
  )

  const ttsContextValue = useMemo(
    (): TtsContextType => ({
      canRead,
      visible,
      showTtsPlayer: initializeTts,
      sentences,
      setSentences: updateSentences,
    }),
    [canRead, visible, sentences, updateSentences, initializeTts],
  )

  return (
    <TtsContext.Provider value={ttsContextValue}>
      {children}
      <StyledBackdrop open={isInitializing}>
        <LoadingSpinner />
      </StyledBackdrop>
      {showHelp && <TtsHelp close={close} />}
      {visible && (
        <TtsPlayer
          disabled={sentences.length === 0}
          close={close}
          playPrevious={playPrevious}
          playNext={playNext}
          isPlaying={isPlaying}
          pause={pause}
          play={startPlaying}
          title={title}
        />
      )}
    </TtsContext.Provider>
  )
}

export default TtsContainer
